import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Baby,
  Briefcase,
  Cake,
  HeartStraight,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { getSupportedEventTypes, getEventTypeConfig } from '../../config/eventTypes';
import EventAwareDatePicker from '../../components/EventAwareDatePicker';
import InfoTip from '../../components/InfoTip';
import EventHome from './EventHome';
import { formatBirthdayNameInput, parseBirthdayNameList, toBirthdayTitleCase } from '../../lib/birthdayEventText';

const WEDDING_NAME_JOINER = ' \u2764\uFE0F ';

const EVENT_TYPE_SHORT_DESCRIPTIONS = {
  wedding: 'Ceremonies, guests, rooms and invites.',
  corporate: 'Summits, offsites, launches and teams.',
  baby_shower: 'Registry, guests, wishes and warm moments.',
  birthday: 'Celebrants, wishes, invites and activities.',
};

const EVENT_TYPE_VISUALS = {
  wedding: {
    icon: HeartStraight,
    cover: '/assets/event-covers/wedding.jpg',
    accent: '#7A214F',
    badge: 'Ceremony',
  },
  corporate: {
    icon: Briefcase,
    cover: '/assets/event-covers/corporate.jpg',
    accent: '#24566B',
    badge: 'Executive',
  },
  baby_shower: {
    icon: Baby,
    cover: '/assets/event-covers/baby-shower.jpg',
    accent: '#8A6F3A',
    badge: 'Gathering',
  },
  birthday: {
    icon: Cake,
    cover: '/assets/event-covers/birthday.jpg',
    accent: '#7C3F73',
    badge: 'Celebration',
  },
};

const buildWeddingEventName = (brideName, groomName) => (
  [brideName.trim(), groomName.trim()]
    .filter(Boolean)
    .join(WEDDING_NAME_JOINER)
);

const buildBirthdayEventName = (honoreeNames) => {
  const names = parseBirthdayNameList(honoreeNames);
  if (names.length === 0) return '';
  if (names.length === 1) return `${names[0]}'s Birthday`;
  return `${names.join(', ')} Birthday`;
};

export default function CreateEvent() {
  const navigate = useNavigate();
  const { loadUser, switchEvent } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [formData, setFormData] = useState({
    event_type: '',
    name: '',
    bride_name: '',
    groom_name: '',
    honoree_names: '',
    start_date: '',
  });
  const eventTypeConfig = getEventTypeConfig(formData.event_type || 'wedding');
  const eventTypes = getSupportedEventTypes();
  const selectedEventVisual = EVENT_TYPE_VISUALS[formData.event_type] || EVENT_TYPE_VISUALS.wedding;
  const SelectedEventIcon = selectedEventVisual.icon;
  const isWedding = formData.event_type === 'wedding';
  const isBirthday = formData.event_type === 'birthday';

  const handleEventTypeChange = (eventTypeId) => {
    setSelectedEventType(eventTypeId);
    setFormData((current) => ({
      ...current,
      event_type: eventTypeId,
    }));
  };

  const normalizeBirthdayNameListField = (field) => {
    if (!isBirthday) return;

    setFormData((current) => ({
      ...current,
      [field]: formatBirthdayNameInput(current[field]),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.start_date) {
      toast.error('Start date is required');
      return;
    }
    if (isWedding && (!formData.bride_name.trim() || !formData.groom_name.trim())) {
      toast.error('Bride and groom names are required');
      return;
    }
    setLoading(true);
    
    try {
      const birthdayHonoreeNames = isBirthday ? parseBirthdayNameList(formData.honoree_names) : [];
      if (isBirthday && birthdayHonoreeNames.length === 0) {
        toast.error('Add at least one birthday celebrant');
        setLoading(false);
        return;
      }
      const eventName = isWedding
        ? buildWeddingEventName(formData.bride_name, formData.groom_name)
        : isBirthday
          ? buildBirthdayEventName(formData.honoree_names)
          : formData.name.trim();
      if (!eventName) {
        toast.error('Event name is required');
        setLoading(false);
        return;
      }

      const eventData = {
        event_type: formData.event_type,
        name: isBirthday ? toBirthdayTitleCase(eventName) : eventName,
        bride_name: isWedding ? formData.bride_name.trim() : null,
        groom_name: isWedding ? formData.groom_name.trim() : null,
        host_names: [],
        honoree_names: isBirthday
          ? birthdayHonoreeNames
          : [],
        milestone_number: null,
        description: null,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: null
      };
      
      const response = await api.post('/events', eventData);
      toast.success('Event created successfully!');

      await switchEvent(response.data.id);
      
      // Reload user to get updated linked_event_ids
      await loadUser();
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <EventHome autoOpenLastEvent={false} />
      <Dialog open onOpenChange={(open) => { if (!open) navigate('/home'); }}>
        <DialogContent
          className="max-h-[92vh] max-w-3xl overflow-y-auto overflow-x-hidden border-white/70 bg-[#fbfaf6] p-0 shadow-2xl"
          closeClassName="right-3 top-3 grid h-9 w-9 place-items-center rounded-full border border-white/80 bg-white/90 text-wedding-charcoal opacity-100 shadow-[0_10px_28px_rgba(0,0,0,0.18)] backdrop-blur transition hover:bg-white hover:text-wedding-charcoal focus:ring-white focus:ring-offset-0 data-[state=open]:bg-white/90 data-[state=open]:text-wedding-charcoal"
          closeIconClassName="h-5 w-5"
        >
          <DialogTitle className="sr-only">Create New Event</DialogTitle>
          <div className="relative">
            <div className="absolute inset-x-0 top-0 h-24 bg-[linear-gradient(135deg,#3f4c42,#66715f_64%,#b99b3e)]" />
            <div className="absolute inset-x-0 top-0 h-24 bg-[radial-gradient(circle_at_12%_16%,rgba(255,255,255,.2),transparent_28%),radial-gradient(circle_at_84%_0%,rgba(255,255,255,.16),transparent_26%)]" />
            <div className="relative space-y-3 p-4 sm:p-5">
              <div className="flex items-start justify-between gap-4 pr-8 text-white">
                <div className="min-w-0">
                  <p className="text-[0.625rem] font-semibold uppercase tracking-[0.28em] text-white/60">Events made easy</p>
                  <div className="mt-1 flex min-w-0 items-center gap-1.5">
                    <h1 className="truncate font-serif text-2xl font-bold text-white" data-testid="create-event-title">Create New Event</h1>
                    <InfoTip content={eventTypeConfig.createEvent.subtitle} label="About creating an event" className="text-white/70 hover:bg-white/10 hover:text-white" />
                  </div>
                </div>
              </div>

        <div className="-mx-4 mt-1 bg-[#fbfaf6] px-4 pb-4 pt-4 sm:-mx-5 sm:px-5 sm:pb-5">
          <div className="pt-1">
            <form onSubmit={handleSubmit} className="space-y-4">
              {!selectedEventType && (
              <div className="space-y-3" data-testid="event-type-step">
                <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                  {eventTypes.map((eventType) => {
                    const visual = EVENT_TYPE_VISUALS[eventType.id] || EVENT_TYPE_VISUALS.wedding;
                    const Icon = visual.icon;
                    const comingSoon = eventType.id === 'corporate';
                    return (
                      <button
                        key={eventType.id}
                        type="button"
                        onClick={() => handleEventTypeChange(eventType.id)}
                        disabled={comingSoon}
                        className={`group relative min-h-[9.75rem] overflow-hidden rounded-xl border border-white/80 bg-wedding-charcoal text-left shadow-[0_12px_32px_rgba(34,42,38,0.12)] transition-all focus-visible:ring-wedding-sage ${comingSoon ? 'cursor-not-allowed grayscale' : 'hover:-translate-y-0.5 hover:border-white hover:shadow-xl'}`}
                        data-testid={`event-type-${eventType.id}`}
                      >
                        <div
                          className={`absolute inset-0 bg-cover bg-center transition-transform duration-500 ${comingSoon ? 'opacity-35' : 'group-hover:scale-105'}`}
                          style={{ backgroundImage: `url(${visual.cover})` }}
                        />
                        <div className={`absolute inset-0 ${comingSoon ? 'bg-slate-800/70' : 'bg-[linear-gradient(180deg,rgba(20,28,24,0.08),rgba(20,28,24,0.42)_42%,rgba(20,28,24,0.82))]'}`} />
                        <div className="absolute inset-x-0 bottom-0 h-20 bg-[radial-gradient(circle_at_18%_100%,rgba(255,255,255,0.22),transparent_52%)]" />
                        <div className="relative flex min-h-[9.75rem] flex-col justify-between p-3.5">
                          <div className="flex items-start justify-between gap-2">
                            <span
                              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur"
                              style={{ color: visual.accent }}
                            >
                              <Icon className="h-5 w-5" weight="duotone" />
                            </span>
                            <span className="rounded-full bg-white/15 px-2 py-1 text-[0.56rem] font-semibold uppercase tracking-[0.14em] text-white/80 backdrop-blur">
                              {visual.badge}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-white drop-shadow-sm sm:text-base">{eventType.createEvent.title}</p>
                            <p className="mt-1 line-clamp-2 text-[0.68rem] leading-4 text-white/75 sm:text-xs">
                              {EVENT_TYPE_SHORT_DESCRIPTIONS[eventType.id] || eventType.createEvent.description}
                            </p>
                          </div>
                        </div>
                        {comingSoon && (
                          <div className="absolute inset-0 z-10 grid place-items-center bg-slate-950/25 backdrop-blur-[1px]">
                            <span className="-rotate-6 rounded-full border border-white/70 bg-white/90 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-700 shadow-lg">
                              Coming soon
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              {selectedEventType && (
              <>
              <div className="relative overflow-hidden rounded-xl border border-white/80 bg-wedding-charcoal px-3 py-3 text-white shadow-[0_12px_30px_rgba(34,42,38,0.12)]">
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-70"
                  style={{ backgroundImage: `url(${selectedEventVisual.cover})` }}
                />
                <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(20,28,24,0.86),rgba(20,28,24,0.62)_56%,rgba(20,28,24,0.36))]" />
                <div className="relative flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur" style={{ color: selectedEventVisual.accent }}>
                    <SelectedEventIcon className="h-5 w-5" weight="duotone" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[0.625rem] font-semibold uppercase tracking-[0.18em] text-white/65">Selected type</p>
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                      <h2 className="font-serif text-xl font-bold leading-none text-white">{eventTypeConfig.createEvent.title}</h2>
                      <span className="text-xs text-white/70">{EVENT_TYPE_SHORT_DESCRIPTIONS[formData.event_type]}</span>
                    </div>
                  </div>
                </div>
              </div>
              {isWedding ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="bride_name">Bride Name *</Label>
                      <Input
                        id="bride_name"
                        placeholder="e.g., Harshada"
                        value={formData.bride_name}
                        onChange={(e) => setFormData({ ...formData, bride_name: e.target.value })}
                        required
                        data-testid="event-bride-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="groom_name">Groom Name *</Label>
                      <Input
                        id="groom_name"
                        placeholder="e.g., Hardik"
                        value={formData.groom_name}
                        onChange={(e) => setFormData({ ...formData, groom_name: e.target.value })}
                        required
                        data-testid="event-groom-name-input"
                      />
                    </div>
                  </div>
                </div>
              ) : isBirthday ? (
                <div className="space-y-2">
                  <Label htmlFor="honoree_names">Who are we celebrating? *</Label>
                  <Input
                    id="honoree_names"
                    placeholder="e.g., Aarav"
                    value={formData.honoree_names}
                    onChange={(e) => setFormData({ ...formData, honoree_names: e.target.value })}
                    onBlur={() => normalizeBirthdayNameListField('honoree_names')}
                    required
                    data-testid="event-honoree-names-input"
                  />
                  <p className="text-xs text-muted-foreground">Use commas, &, /, or and between names for a joint birthday.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="name">Event Name *</Label>
                  <Input
                    id="name"
                    placeholder={eventTypeConfig.createEvent.namePlaceholder}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="event-name-input"
                  />
                </div>
              )}

              <EventAwareDatePicker
                id="event-date"
                label="Event date"
                value={formData.start_date}
                onChange={(date) => setFormData((current) => ({ ...current, start_date: date }))}
                defaultToEventStart={false}
                required
                data-testid="event-date-input"
              />

              <div className="flex gap-3 pt-4">
                <Link to="/home" className="flex-1">
                  <Button type="button" variant="outline" className="w-full">
                    Cancel
                  </Button>
                </Link>
                <Button 
                  type="submit" 
                  className="flex-1 bg-wedding-sage hover:bg-wedding-sage/90"
                  disabled={loading}
                  data-testid="create-event-submit"
                >
                  {loading ? 'Creating...' : 'Create Event'}
                </Button>
              </div>
              </>
              )}
            </form>
          </div>
            </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
